import { useAppSelector } from '@state/redux/store';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { GoDotFill } from 'react-icons/go';
import { GrAttachment } from 'react-icons/gr';
import { IoIosSend } from 'react-icons/io';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import './ChatBox.scss';

import AudioPlayer from '@components/Projects/TakeProject/Elements/AudioPlayer';
import { nanoid } from '@reduxjs/toolkit';
import { translate } from '@state/utils/helper';
import classNames from 'classnames';
import moment from 'moment';
import Markdown from 'react-markdown';
import ReactPlayer from 'react-player';
import ReactTextareaAutosize from 'react-textarea-autosize';
import videoIcon from '../../assets/icons/videoIcon.png';
import type { ChatItem } from './TempChat';

function getMediaType(url: string) {
  if (url.includes('.mp4') || url.includes('vimeo.com')) return 'video';
  if (url.includes('.mp3') || url.includes('.wav')) return 'audio';
  return 'text';
}
interface IAvatar {
  id: string;
  name: string;
  profileImage: string;
  designation: string;
  personality: string;
  loading?: boolean;
}
const ChatBox = (props: {
  avatar: IAvatar;
  messages: ChatItem[];
  setMessages?: Dispatch<SetStateAction<ChatItem[]>>;
  disabled?: boolean;
  loading?: boolean;
}) => {
  const { messages = [], avatar, setMessages, disabled = false, loading = false } = props;
  const [inputValue, setInputValue] = useState('');
  const online = !disabled;
  const [textareaHeight, setTextareaHeight] = useState(0);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (disabled || !inputValue.trim()) return;
      e.preventDefault();
      setMessages &&
        setMessages((prev) => [
          ...prev,
          {
            id: nanoid(),
            message: inputValue.trim(),
            sender: 'user',
            timestamp: new Date().toISOString(),
            openedAt: null,
          },
        ]);
      setInputValue('');
    },
    [disabled, inputValue, setMessages]
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  const handleHeightChange = useCallback((height: number) => {
    const newHeight = Math.min(height + 64, 400);
    setTextareaHeight(newHeight);
  }, []);

  // Memoize form classes
  const formClasses = useMemo(
    () =>
      classNames(
        'chatbot-search-box-container',
        disabled ? 'chatbot-search-box-container-disabled' : null
      ),
    [disabled]
  );

  // Memoize placeholder text
  const placeholderText = useMemo(
    () => (disabled ? translate('chat.chatEndedOrLocked') : translate('chat.typeAMessage')),
    [disabled]
  );
  return (
    <div className="chatbox-container">
      <div className="chatbox-header">
        <div className="chatbox-header-title">
          <img src={avatar.profileImage} alt="Avatar" className="avatar rounded-circle" />
          <div>
            <h1>{avatar.name}</h1>
            <h2>
              {online ? (
                <>
                  <GoDotFill color="#2dce89" size={20} />
                  {translate('chat.online')}
                </>
              ) : (
                <>
                  <GoDotFill color="#cbcbcb" size={20} />
                  {translate('chat.offline')}
                </>
              )}
            </h2>
          </div>
        </div>
        <div className="chatbox-header-right">
          <BsThreeDotsVertical size={24} />
        </div>
      </div>

      <div className="chatbox-body">
        <div
          className="chatbox__body__container"
          style={{ height: `calc(-${textareaHeight.toString()}px + 100vh - 200px)` }}
        >
          <div className="chatbox__body__container__inner">
            {messages.map((message) => {
              return (
                <Message message={message} key={message.id} assistantAvatar={avatar.profileImage} />
              );
            })}
            {loading ? (
              <div className="d-flex align-items-center mt-4">
                <div className="chatbox__bubble__container chatbox__bubble__container__assistant">
                  <div className="chatbox__bubble__container__assistant__inner">
                    <div>
                      <span className="chatbox__bubble__container__assistant__loading">
                        {Loading}
                      </span>
                    </div>
                  </div>
                  <img
                    src={avatar.profileImage}
                    alt="My profile"
                    className="chatbox__bubble__container__assistant__img"
                    width={144}
                    height={144}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="chatbox-footer">
          <form className={formClasses}>
            <div style={{ display: 'flex', gap: '0.2rem', alignItems: 'center', flex: '1' }}>
              <div>
                <GrAttachment className="chatbot-search-box-fixed-label" size={24} />
              </div>
              <div className="chatbot-input-wrapper">
                <ReactTextareaAutosize
                  id="essay-copilot-pane__input"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  maxRows={10}
                  onHeightChange={handleHeightChange}
                  placeholder={placeholderText}
                  onPaste={(e) => {
                    e.preventDefault();
                  }}
                  disabled={disabled}
                ></ReactTextareaAutosize>
              </div>
            </div>
            <div>
              <button
                className="essay-copilot-pane__sendMessageButton"
                type="submit"
                onClick={handleSubmit}
                disabled={disabled}
              >
                <IoIosSend size={32} color="0ea5e9" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
export default ChatBox;

const Message = memo(function Message(props: { message: ChatItem; assistantAvatar: string }) {
  const { message, assistantAvatar } = props;
  const user = useAppSelector((state) => state.users);
  const messageBubbleRef = useRef<HTMLDivElement>(null);

  const messageType = useMemo(
    () => message.simulationChatItem?.attributes.itemType ?? getMediaType(message.message),
    [message.simulationChatItem?.attributes.itemType, message.message]
  );

  useEffect(() => {
    if (messageBubbleRef.current) {
      const timeoutId = setTimeout(() => {
        messageBubbleRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [message]);

  const renderMedia = useCallback(() => {
    switch (messageType) {
      case 'video':
        return (
          <div className="chatbox_video_container">
            <div className="chatbox_video_subtitle_container">
              <img className="chatbox_video_subtitle_image" src={videoIcon} alt="video icon" />
              <div className="chatbox_video_subtitle_text_container">
                <div className="chatbox_video_subtitle_text">{translate('chat.videoMessage')}</div>
                <div className="chatbox_video_subtitle_subtext">
                  <div className="chatbox_video_subtitle_recorded_container">
                    <GoDotFill color="#cbcbcb" size={20} />
                    <div className="sdf">{translate('chat.recorded')}</div>
                  </div>
                  <div>{moment(message.timestamp).format('h:mm A')}</div>
                </div>
              </div>
            </div>
            <ReactPlayer
              url={message.simulationChatItem?.attributes.data.url}
              volume={0.8}
              muted={false}
              playing={false}
              controls
              width="100%"
              height={240}
              config={{
                vimeo: {
                  playerOptions: {
                    title: true,
                    byline: false,
                    portrait: false,
                    vimeoLogo: false,
                  },
                },
              }}
            />
          </div>
        );
      case 'audio':
        return <AudioPlayer audioUrl={message.simulationChatItem?.attributes.data.url ?? ''} />;
      default:
        return (
          <>
            <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{message.message}</Markdown>
            <div className="chatbox__bubble__container__assistant__timestamp">
              {moment(message.timestamp).format('h:mm A')}
            </div>
          </>
        );
    }
  }, [messageType, message]);
  if (message.sender === 'assistant') {
    return (
      <div
        key={message.id}
        className="chatbox__bubble__container chatbox__bubble__container__assistant"
        ref={messageBubbleRef}
      >
        <div className="chatbox__bubble__container__assistant__inner">
          <div>
            <span className="chatbox__bubble__container__assistant__text">{renderMedia()}</span>
          </div>
        </div>
        <img
          src={message.simulationChatItem?.attributes.senderImage ?? assistantAvatar}
          alt="My profile"
          className="chatbox__bubble__container__assistant__img"
          width={144}
          height={144}
        />
      </div>
    );
  }
  if (message.sender === 'user') {
    return (
      <div
        key={message.id}
        className="chatbox__bubble__container chatbox__bubble__container__user"
        ref={messageBubbleRef}
      >
        <div className="chatbox__bubble__container__user__inner">
          <div>
            <span className="chatbox__bubble__container__user__text">
              <span className="chatbox__bubble__container__user__markdown">
                <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{message.message}</Markdown>
              </span>
              <div className="chatbox__bubble__container__user__timestamp">
                {moment(message.timestamp).format('h:mm A')}
              </div>
            </span>
          </div>
        </div>
        <img
          src={message.simulationChatItem?.attributes.senderImage ?? user.user.attributes.avatarUrl}
          alt="My profile"
          className="chatbox__bubble__container__user__img"
          width={144}
          height={144}
        />
      </div>
    );
  }
  return null;
});

const Loading = (
  <svg height="40" width="45" className="typing-loader">
    <circle className="typing-dot" cx="10" cy="20" r="4" />
    <circle className="typing-dot" cx="25" cy="20" r="4" />
    <circle className="typing-dot" cx="40" cy="20" r="4" />
  </svg>
);
